import { clientLogger } from '@/lib/clientLogger';
import { useState, useEffect, useCallback } from 'react';
import * as signalR from '@microsoft/signalr';
import { App as CapacitorApp } from '@capacitor/app';
import type { PluginListenerHandle } from '@capacitor/core';
import { authService } from '@/lib/firebase';
import { ENV } from '@/config/environment';
import { apiService } from '@/lib/api';
import { isIOS, isNative } from '@/lib/capacitor';

// withAutomaticReconnect gives up after its last delay and fires onclose. Nothing used to
// restart it after that, so any outage longer than ~47s left realtime silently dead for the
// rest of the session. These back off a cold restart instead; capped so an offline device
// settles at one attempt a minute rather than spinning.
const RESTART_DELAYS_MS = [2000, 5000, 15000, 30000, 60000];

type ConnectionStatus = 'disconnected' | 'connecting' | 'connected' | 'reconnecting';

interface UseRealtimeConnectionReturn {
    connection: signalR.HubConnection | null;
    status: ConnectionStatus;
}

/**
 * Hook to establish and manage a SignalR connection to the Realtime service.
 * Automatically connects when authenticated and reconnects on failure.
 *
 * Usage:
 *   const { connection, status } = useRealtimeConnection();
 *
 *   useEffect(() => {
 *     if (!connection) return;
 *     connection.on("JobUpdated", (event) => {
 *       // Handle invalidation
 *     });
 *     return () => connection.off("JobUpdated");
 *   }, [connection]);
 */
export const useRealtimeConnection = (): UseRealtimeConnectionReturn => {
    const [status, setStatus] = useState<ConnectionStatus>('disconnected');
    const [connection, setConnection] = useState<signalR.HubConnection | null>(null);

    const getToken = useCallback(async (): Promise<string> => {
        const token = await authService.getCurrentToken();
        if (!token) throw new Error('Not authenticated');
        return token;
    }, []);

    // Read orgId once at hook render time so connection rebuilds on org switch
    const orgId = apiService.getOrgId();

    useEffect(() => {
        let isMounted = true;
        let retryTimeout: ReturnType<typeof setTimeout> | null = null;
        let startTimeout: ReturnType<typeof setTimeout> | null = null;
        let restartAttempt = 0;

        // Browsers can't send headers on a WebSocket, so the org also rides in the URL; the gateway
        // validates it. Without it the server falls back to the org in the login token, which is
        // wrong for users in more than one org.
        const hubUrl = orgId
            ? `${ENV.API.BASE_URL}/realtime?org_id=${encodeURIComponent(orgId)}`
            : `${ENV.API.BASE_URL}/realtime`;

        const connection = new signalR.HubConnectionBuilder()
            .withUrl(hubUrl, {
                accessTokenFactory: getToken,
                headers: orgId ? { 'x-org-id': orgId } : {},
                // iOS WKWebView (served from capacitor:// custom scheme) has a WebKit bug where
                // WebSocket connections hang instead of erroring, so SignalR never falls back.
                // ponytail: skip WS on iOS only; drop this once WKWebView WS works from custom schemes.
                ...(isIOS()
                    ? { transport: signalR.HttpTransportType.ServerSentEvents | signalR.HttpTransportType.LongPolling }
                    : {}),
            })
            .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
            .configureLogging(signalR.LogLevel.Information)
            .build();

        connection.onreconnecting(() => {
            if (!isMounted) return;
            setStatus('reconnecting');
        });

        connection.onreconnected(() => {
            if (!isMounted) return;
            restartAttempt = 0;
            setStatus('connected');
        });

        const clearRetry = () => {
            if (retryTimeout) {
                clearTimeout(retryTimeout);
                retryTimeout = null;
            }
        };

        const scheduleRestart = () => {
            if (!isMounted || retryTimeout) return;
            const delay = RESTART_DELAYS_MS[Math.min(restartAttempt, RESTART_DELAYS_MS.length - 1)];
            restartAttempt++;
            retryTimeout = setTimeout(() => {
                retryTimeout = null;
                void startConnection();
            }, delay);
        };

        const startConnection = async () => {
            if (!isMounted) return;
            // start() throws if the hub is already connected or connecting, and the
            // foreground/online listeners below can both fire for the same recovery.
            if (connection.state !== signalR.HubConnectionState.Disconnected) return;
            try {
                setStatus('connecting');
                await connection.start();
                if (!isMounted) return;
                restartAttempt = 0;
                setStatus('connected');
            } catch (err) {
                if (!isMounted) return;
                clientLogger.debug('[Realtime] Connection failed:', err);
                setStatus('disconnected');
                scheduleRestart();
            }
        };

        connection.onclose(() => {
            if (!isMounted) return;
            clientLogger.info('[Realtime] Connection closed');
            setStatus('disconnected');
            scheduleRestart();
        });

        // Foregrounding is the strongest "the transport is probably dead, and the network is
        // probably back" signal there is — iOS suspends the WebView on background, which kills
        // the SSE/long-polling stream every single time. Skip the backoff and retry at once.
        const reconnectNow = () => {
            if (!isMounted) return;
            if (connection.state !== signalR.HubConnectionState.Disconnected) return;
            clearRetry();
            restartAttempt = 0;
            void startConnection();
        };

        const onVisibilityChange = () => {
            if (document.visibilityState === 'visible') reconnectNow();
        };
        document.addEventListener('visibilitychange', onVisibilityChange);

        // visibilitychange does fire in the Capacitor WebView, but appStateChange is the
        // documented native signal and this is the one fix worth belt-and-braces.
        // reconnectNow is idempotent, so a double fire is a no-op.
        let appStateListener: PluginListenerHandle | null = null;
        let appStateListenerDetached = false;
        if (isNative()) {
            void CapacitorApp.addListener('appStateChange', ({ isActive }) => {
                if (isActive) reconnectNow();
            }).then((handle) => {
                if (appStateListenerDetached) void handle.remove();
                else appStateListener = handle;
            });
        }

        setConnection(connection);
        startTimeout = setTimeout(() => {
            void startConnection();
        }, 0);

        return () => {
            isMounted = false;
            if (startTimeout) clearTimeout(startTimeout);
            clearRetry();
            document.removeEventListener('visibilitychange', onVisibilityChange);
            appStateListenerDetached = true;
            void appStateListener?.remove();
            setConnection(null);
            connection.stop();
        };
    }, [getToken, orgId]);

    return {
        connection,
        status,
    };
};
